/**
 * D3 Planet Sprite Manager
 * 
 * Manages planet sprites with D3 data binding and selection patterns
 * Integrates with existing universal architecture and optimized utilities
 */

import * as d3 from 'd3';
import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { PlanetSprite, D3PlanetAnimator, AnimationConfig } from './D3PlanetAnimator';
import { getElementType, getElementColor, getPlanetDisplayName } from '../config/PlanetLayerConfig';
import { getRetrogradeDisplayText } from '../../DataSections/helpers/planetDisplayHelper';
import { globalEventStream, createEvent } from '../core/EventStream';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';
import { FONTS } from '../../../../../config/fonts';
import { DisplayModeService } from '../../services/DisplayModeService';

export interface PlanetSpriteConfig {
  radius: number;
  strokeWidth: number;
  strokeColor: string;
  fillColor: string;
  textColor: string;
  connectionLineLength: number;
}

export interface SpriteUpdateData {
  planetName: string;
  position: { x: number, y: number };
  rotation?: number;
  visible?: boolean;
  isRetrograde?: boolean;
}

export class D3PlanetSpriteManager {
  private sprites: Map<string, PlanetSprite> = new Map();
  private linesOverlay: d3.Selection<SVGGElement, unknown, any, any>;
  private spriteGroup: d3.Selection<SVGGElement, unknown, any, any>;
  private chartId?: string; // Chart ID for display mode settings
  private debugMode = false; // Debug logs disabled for production
  private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];
  
  // Default sprite configuration - dynamic from preset
  private static getDefaultConfig(): PlanetSpriteConfig {
    const currentPreset = chartColorPresetManager.getCurrentPreset();
    const sprites = currentPreset.planetSprites;
    return {
      radius: sprites.radius,
      strokeWidth: sprites.strokeWidth,
      strokeColor: currentPreset.degreeLines.mainStroke,
      fillColor: sprites.fillColor,
      textColor: currentPreset.elements.planetDefault,
      connectionLineLength: sprites.connectionLineLength
    };
  }

  // Get mode-specific text settings from preset
  private static getModeTextConfig(mode: 'symbols' | 'short' | 'full'): { fontSize: number; fontWeight: number; textStroke?: number; fontFamily?: string; dy?: number } {
    const currentPreset = chartColorPresetManager.getCurrentPreset();
    return currentPreset.planetSprites.modes[mode];
  }

  // Legacy static access for backward compatibility
  private static readonly DEFAULT_CONFIG = D3PlanetSpriteManager.getDefaultConfig();

  constructor(svgContainer: d3.Selection<SVGSVGElement, unknown, any, any>, chartId?: string) {
    this.chartId = chartId;

    // Use unique ID-based selectors instead of complex class selectors
    const linesOverlayId = this.chartId ? `lines-overlay-${this.chartId}` : 'lines-overlay';
    const realLinesOverlay = document.getElementById(linesOverlayId);

    if (realLinesOverlay) {
      // Use existing element from real DOM
      this.linesOverlay = d3.select(realLinesOverlay as unknown as SVGGElement) as d3.Selection<SVGGElement, unknown, any, any>;
      if (this.debugMode) devLog(`🎭 [D3PlanetSpriteManager] Found existing lines-overlay: ${linesOverlayId}`);
    } else {
      // Create new with unique ID
      const viewportId = this.chartId ? `viewport-content-${this.chartId}` : 'viewport-content';
      const viewportContent = svgContainer.select(`#${viewportId}`);

      if (!viewportContent.empty()) {
        this.linesOverlay = viewportContent
          .append('g')
          .attr('id', linesOverlayId)
          .classed('lines-overlay', true);
        if (this.debugMode) devLog(`🎭 [D3PlanetSpriteManager] Created new lines-overlay: ${linesOverlayId}`);
      } else {
        throw new Error(`D3PlanetSpriteManager: Viewport with ID ${viewportId} not found in SVG`);
      }
    }
    
    // Use unique ID for sprite group instead of complex class selector
    const spriteGroupId = this.chartId ? `planet-sprites-${this.chartId}` : 'planet-sprites';
    const existingSpriteGroup = document.getElementById(spriteGroupId);

    if (existingSpriteGroup) {
      // Reuse existing sprite group
      this.spriteGroup = d3.select(existingSpriteGroup as unknown as SVGGElement) as d3.Selection<SVGGElement, unknown, any, any>;
      if (this.debugMode) devLog(`🎭 [D3PlanetSpriteManager] Found existing sprite group: ${spriteGroupId}`);
    } else {
      // Create dedicated sprite group with unique ID
      if (this.debugMode) devLog(`🎭 [D3PlanetSpriteManager] Creating new sprite group: ${spriteGroupId}`);
      if (this.debugMode) devLog(`🔍 [D3PlanetSpriteManager] Lines overlay exists:`, !!this.linesOverlay.node());
      if (this.debugMode) devLog(`🔍 [D3PlanetSpriteManager] Lines overlay parent:`, this.linesOverlay.node()?.parentElement);

      this.spriteGroup = this.linesOverlay
        .append('g')
        .attr('id', spriteGroupId)
        .classed('planet-sprites-group', true)
        .attr('data-layer', 'planet-sprites')
        .attr('data-chart-id', this.chartId || '');

      if (this.debugMode) devLog(`🎭 [D3PlanetSpriteManager] Created sprite group with ID: ${spriteGroupId}`);

      // Verify group was added to DOM immediately after creation
      const immediateVerify = this.spriteGroup.node();
      if (this.debugMode) devLog(`🔍 [D3PlanetSpriteManager] Immediate verify - sprite group node:`, !!immediateVerify);
      if (this.debugMode) devLog(`🔍 [D3PlanetSpriteManager] Immediate verify - has parent:`, !!immediateVerify?.parentElement);

      // Subscribe to containers.ready event for DOM verification
      this.subscribeToContainerEvents();
    }
  }


  /**
   * Create planet sprite with unified sprite approach
   */
  public createPlanetSprite(
    planetName: string,
    containerIndex: number,
    initialPosition: { x: number, y: number },
    config: Partial<PlanetSpriteConfig> = {},
    planetData?: { name: string; shortName: string },
    horaCurrentPlanet?: string
  ): PlanetSprite {

    // Direct element type detection using both short and full names
    const fullName = planetData?.name || planetName;
    const elementType = getElementType(planetName, horaCurrentPlanet, fullName);

    const planetColor = getElementColor(elementType);

    
    // Apply color-specific configuration
    const colorConfig = {
      textColor: planetColor
    };
    
    const finalConfig = {
      ...D3PlanetSpriteManager.getDefaultConfig(),
      ...colorConfig,
      ...config // User config overrides automatic color
    };

    
    // Create sprite container group (position only, no rotation)
    const container = this.spriteGroup
      .append('g')
      .classed('planet-sprite', true)
      .attr('data-planet', planetName)
      .attr('data-house', containerIndex.toString())
      .attr('transform', `translate(${initialPosition.x}, ${initialPosition.y})`)
      .attr('visibility', 'visible')  // Force visibility
      .attr('opacity', 1);            // Ensure not transparent
    
    
    // Verify sprite was added to DOM
    const verifySpriteInDOM = document.querySelector(`[data-planet="${planetName}"].planet-sprite`);
    
    // Create separate group for connection line (will be rotated independently)
    const connectionGroup = container
      .append('g')
      .classed('connection-group', true);
    
    // Create connection line in its own group (so it can be rotated separately)
    const connectionLine = connectionGroup
      .append('line')
      .classed('planet-connection-line', true)
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', -finalConfig.connectionLineLength)
      .attr('stroke', finalConfig.strokeColor)
      .attr('stroke-width', finalConfig.strokeWidth)
      .attr('opacity', 1);
    
    // Create planet circle (in main container, no rotation)
    const circle = container
      .append('circle')
      .classed('planet-circle', true)
      .attr('r', finalConfig.radius)
      .attr('fill', finalConfig.fillColor)
      .attr('stroke', finalConfig.strokeColor)
      .attr('stroke-width', finalConfig.strokeWidth)
      .attr('opacity', 1);
    
    // Create planet text (in main container, no rotation) - use current display mode
    const currentDisplayMode = DisplayModeService.getDisplayMode(this.chartId);
    const displayName = getPlanetDisplayName(planetName, currentDisplayMode);
    const modeTextConfig = D3PlanetSpriteManager.getModeTextConfig(currentDisplayMode);
    // Lg (Lagna) doesn't need vertical offset - it's already centered
    const dyOffset = (planetName === 'Lg') ? 0 : (modeTextConfig.dy || 0);
    const text = container
      .append('text')
      .classed('planet-text', true)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('dy', dyOffset)
      .attr('font-size', modeTextConfig.fontSize)
      .attr('font-weight', modeTextConfig.fontWeight)
      .attr('font-family', modeTextConfig.fontFamily || FONTS.chart.primary)
      .attr('fill', finalConfig.textColor)
      .attr('stroke', modeTextConfig.textStroke ? finalConfig.textColor : 'none')
      .attr('stroke-width', modeTextConfig.textStroke || 0)
      .attr('opacity', 1)
      .attr('visibility', 'visible')  // Force visibility
      .attr('display', 'block')       // Ensure not hidden
      .text(displayName);

    // Ensure sprite group is visible
    this.spriteGroup
      .attr('visibility', 'visible')
      .attr('opacity', 1)
      .attr('display', 'block');

    const spriteGroupNode = this.spriteGroup.node();

    // Check if sprite group is actually connected to DOM
    if (!spriteGroupNode || !spriteGroupNode.parentElement) {
        devError(`❌ [D3PlanetSpriteManager] CRITICAL: Sprite group not connected to DOM for chart ${this.chartId}`);
        devLog(`🔍 [D3PlanetSpriteManager] Attempting DOM reconnection...`);

        // Find parent using ID-based selectors
        const linesOverlayId = this.chartId ? `lines-overlay-${this.chartId}` : 'lines-overlay';
        const linesOverlay = document.getElementById(linesOverlayId);

        if (this.debugMode) devLog(`🔍 [D3PlanetSpriteManager] Lines overlay found:`, !!linesOverlay);

        if (linesOverlay && spriteGroupNode) {
            if (this.debugMode) devLog(`🔧 [D3PlanetSpriteManager] Manually attaching sprite group to lines overlay`);
            linesOverlay.appendChild(spriteGroupNode);
            if (this.debugMode) devLog(`✅ [D3PlanetSpriteManager] Sprite group manually attached to ${linesOverlayId}`);
        }
    }

    if (this.debugMode) devLog(`🔧 [D3PlanetSpriteManager] Forced sprite group visibility for ${planetName}`);

    // Create sprite object
    const sprite: PlanetSprite = {
      container,
      connectionGroup,
      circle,
      text,
      connectionLine,
      position: initialPosition,
      rotation: 0,
      side: 'top',
      planetName,
      containerIndex,
      horaCurrentPlanet: horaCurrentPlanet,  // Store hora current planet for color updates
      planetFullName: fullName               // Store full planet name for hora detection
    };
    
    // Store sprite in registry
    this.sprites.set(planetName, sprite);

    // Force DOM synchronization and reconnection
    this.ensureSpriteGroupConnection();

    // Emit event that sprite has been created (event-driven approach)
    this.emitSpriteEvent('sprite.created', {
      planetName,
      containerIndex,
      position: initialPosition
    });

    // Note: Individual sprite verification removed to avoid excessive events
    // DOM verification will be handled by the containers.ready event system

    if (this.debugMode) devLog(`🎨 [D3PlanetSpriteManager] Created sprite for ${planetName}`, {
      chartId: this.chartId,
      position: initialPosition,
      containerIndex,
      spriteInDOM: !!document.querySelector(`[data-planet="${planetName}"].planet-sprite`),
      spriteGroupExists: !!this.spriteGroup?.node(),
      totalSprites: this.sprites.size
    });

    return sprite;
  }

  /**
   * Get existing sprite by planet name
   */
  public getSprite(planetName: string): PlanetSprite | undefined {
    return this.sprites.get(planetName);
  }

  /**
   * Get all sprites
   */
  public getAllSprites(): Map<string, PlanetSprite> {
    return new Map(this.sprites);
  }


  /**
   * Batch update multiple sprites efficiently
   */

  public updateAllSprites(
    updates: SpriteUpdateData[],
    animationConfig: AnimationConfig = D3PlanetAnimator.ANIMATION_PRESETS.FAST
  ): Promise<void[]> {

    const updateOperations = updates
      .map(update => {
        const sprite = this.sprites.get(update.planetName);
        if (!sprite) {
          devWarn(`[D3SpriteManager] Sprite not found for update: ${update.planetName}`);
          return null;
        }

        return {
          sprite,
          position: update.position,
          rotation: update.rotation ?? sprite.rotation,  // Use nullish coalescing to handle rotation=0
          visible: update.visible !== undefined ? update.visible : true,
          isRetrograde: update.isRetrograde,
          planetName: update.planetName
        };
      })
      .filter(op => op !== null);

    // Handle visibility changes immediately
    updateOperations.forEach(op => {
      if (op && 'visible' in op) {
        op.sprite.container.style('opacity', op.visible ? 1 : 0);
      }
    });

    // Handle retrograde text updates using DRY helper
    const currentDisplayMode = DisplayModeService.getDisplayMode(this.chartId);
    updateOperations.forEach(op => {
      if (op) {
        const baseDisplayName = getPlanetDisplayName(op.planetName, currentDisplayMode);
        const displayText = getRetrogradeDisplayText(op.planetName, baseDisplayName, op.isRetrograde || false);
        op.sprite.text.text(displayText);
      }
    });

    // Use D3PlanetAnimator for position/rotation updates
    return D3PlanetAnimator.updateMultipleSprites(
      updateOperations.map(op => ({
        sprite: op!.sprite,
        position: op!.position,
        rotation: op!.rotation
      })),
      animationConfig
    );
  }


  /**
   * Clear all sprites
   */
  public clearAllSprites(): void {
    devLog(`[D3PlanetSpriteManager] 🧹 clearAllSprites called, sprites: ${this.sprites.size}`);

    this.sprites.forEach((sprite, planetName) => {
      devLog(`[D3PlanetSpriteManager] 🗑️ Removing: ${planetName}`);
      sprite.container.remove();
    });

    this.sprites.clear();
    devLog('[D3PlanetSpriteManager] ✅ All sprites cleared');
  }

  /**
   * Get sprite statistics for debugging
   */
  public getSpriteStats(): {
    total: number;
    visible: number;
    byContainer: Record<number, number>;
  } {
    const stats = {
      total: this.sprites.size,
      visible: 0,
      byContainer: {} as Record<number, number>
    };

    this.sprites.forEach(sprite => {
      // Check visibility
      const opacity = parseFloat(sprite.container.style('opacity') || '1');
      if (opacity > 0) {
        stats.visible++;
      }

      // Count by container
      const containerIndex = sprite.containerIndex;
      stats.byContainer[containerIndex] = (stats.byContainer[containerIndex] || 0) + 1;
    });

    return stats;
  }

  /**
   * Find sprites by container index
   */
  public getSpritesByContainer(containerIndex: number): PlanetSprite[] {
    const sprites: PlanetSprite[] = [];
    
    this.sprites.forEach(sprite => {
      if (sprite.containerIndex === containerIndex) {
        sprites.push(sprite);
      }
    });
    
    return sprites;
  }


  /**
   * Update sprite colors based on planet types
   */
  public updatePlanetColors(): void {
    const newConfig = D3PlanetSpriteManager.getDefaultConfig();

    this.sprites.forEach((sprite, planetName) => {
      // Use both short name and full name for hora detection
      const elementType = getElementType(planetName, sprite.horaCurrentPlanet, sprite.planetFullName);

      const planetColor = getElementColor(elementType);

      // Update text color (element-specific)
      sprite.text.attr('fill', planetColor);

      // Update circle stroke color (from preset)
      sprite.circle.attr('stroke', newConfig.strokeColor);

    });
  }


  /**
   * Console testing method for color functionality
   */
  public testPlanetColors(): void {
    devLog('🎨 Testing planet color system:');
    this.sprites.forEach((sprite, planetName) => {
      const elementType = getElementType(planetName);
      const planetColor = getElementColor(elementType);
      const currentColor = sprite.text.attr('fill');
      devLog(`  ${planetName}: type=${elementType}, expected=${planetColor}, current=${currentColor}`);
    });
  }

  /**
   * Subscribe to container events for DOM verification
   */
  private subscribeToContainerEvents(): void {
    // Subscribe to containers.ready events for this chart
    const subscription = globalEventStream.subscribe('containers.ready', (event: any) => {
      if (event.chartId === this.chartId) {
        this.verifyDOMConnection();
      }
    }, {
      sourceFilter: `tab-chart-renderer-${this.chartId}`
    });
    this.eventSubscriptions.push(subscription);

    // Note: Removed individual sprite verification subscriptions to avoid excessive processing
    // DOM verification is handled efficiently by the containers.ready event
  }

  /**
   * Verify DOM connection and emit events about sprite status
   */
  private verifyDOMConnection(): void {
    const spriteGroupId = this.chartId ? `planet-sprites-${this.chartId}` : 'planet-sprites';
    const domSpriteGroup = document.getElementById(spriteGroupId);
    const isConnected = !!domSpriteGroup && !!this.spriteGroup?.node();


    if (!isConnected) {
      // Attempt auto-repair
      const repairSuccessful = this.ensureSpriteGroupConnection();
      if (repairSuccessful) {
        this.emitSpriteEvent('sprite.dom.connected');
      } else {
        this.emitSpriteEvent('sprite.dom.disconnected');
      }
    } else {
      this.emitSpriteEvent('sprite.dom.connected');
    }

    // Verify individual sprites in DOM
    this.verifyAllSpritesInDOM();
  }

  /**
   * Verify all sprites are properly connected to DOM
   */
  private verifyAllSpritesInDOM(): void {
    this.sprites.forEach((_sprite, planetName) => {
      const spriteInDOM = document.querySelector(`[data-planet="${planetName}"].planet-sprite`);
      const isConnected = !!spriteInDOM;


      if (!isConnected) {
        this.ensureSpriteGroupConnection();

        // Re-verify after repair attempt
        // const repairedSprite = document.querySelector(`[data-planet="${planetName}"].planet-sprite`);
      }
    });
  }

  /**
   * Emit sprite-related events
   */
  private emitSpriteEvent(eventType: string, data: any = {}): void {
    const event = createEvent(eventType, `d3-sprite-manager-${this.chartId}`, {
      chartId: this.chartId,
      spriteCount: this.sprites.size,
      ...data
    });
    globalEventStream.emit(event);
  }

  /**
   * Ensure sprite group is properly connected to the DOM viewport
   */
  private ensureSpriteGroupConnection(): boolean {
    const spriteGroupId = this.chartId ? `planet-sprites-${this.chartId}` : 'planet-sprites';
    const viewportId = this.chartId ? `viewport-content-${this.chartId}` : 'viewport-content';

    // Check if sprite group exists in DOM
    const domSpriteGroup = document.getElementById(spriteGroupId);
    const viewport = document.getElementById(viewportId);

    if (!viewport) {
      devWarn(`🔧 [D3PlanetSpriteManager] Viewport ${viewportId} not found, cannot reconnect sprite group`);
      return false;
    }

    if (!domSpriteGroup) {

      // Find or create lines-overlay within the viewport
      let linesOverlay = viewport.querySelector(`#lines-overlay-${this.chartId}`);


      if (!linesOverlay) {
        // Create lines-overlay if it doesn't exist
        const viewportD3 = d3.select(viewport);
        const linesOverlayD3 = viewportD3
          .append('g')
          .attr('id', `lines-overlay-${this.chartId}`)
          .classed('lines-overlay', true);

        linesOverlay = linesOverlayD3.node() as Element;
      } else {
      }

      if (linesOverlay && this.spriteGroup?.node()) {
        linesOverlay.appendChild(this.spriteGroup.node()!);
        return true;
      } else {
        devWarn(`🔧 [D3PlanetSpriteManager] Cannot reconnect: linesOverlay=${!!linesOverlay}, spriteGroupNode=${!!this.spriteGroup?.node()}`);
        return false;
      }
    } else {
      // Sprite group exists, verify D3 connection
      if (this.spriteGroup?.node() !== (domSpriteGroup as any)) {
        this.spriteGroup = d3.select(domSpriteGroup as any);
      }
      return true;
    }
  }

  /**
   * Get DOM element path for debugging - simplified using IDs
   */
  private getElementPath(element: Element): string {
    const path: string[] = [];
    let current: Element | null = element;

    while (current && current !== document.body) {
      let selector = current.tagName.toLowerCase();

      // Prefer ID over classes since we have unique chart IDs
      if (current.id) {
        selector += `#${current.id}`;
        // If we found an ID, we can stop here as it's unique
        path.unshift(selector);
        break;
      }

      // Add data attributes for better identification
      const planetData = (current as any).getAttribute?.('data-planet');
      const chartData = (current as any).getAttribute?.('data-chart-id');
      if (planetData) selector += `[data-planet="${planetData}"]`;
      if (chartData) selector += `[data-chart-id="${chartData}"]`;

      path.unshift(selector);
      current = current.parentElement;
    }

    return path.join(' > ');
  }

  /**
   * Dispose manager and clean up all resources
   */
  public dispose(): void {
    // Unsubscribe from all event subscriptions
    this.eventSubscriptions.forEach(sub => sub.unsubscribe());
    this.eventSubscriptions = [];

    this.clearAllSprites();

    // Remove sprite group from DOM
    this.spriteGroup.remove();
  }
}

// Export to browser console for testing
if (typeof window !== 'undefined') {
  (window as any).D3PlanetSpriteManager = D3PlanetSpriteManager;
}